import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner, Badge } from 'react-bootstrap';
import { showToast as toast } from '../toastManager';
import axios from 'axios';

const WEBSITE_PAGES_LIMIT = 7;
const LANDING_PAGES_LIMIT = 3;

// Polling tuning for the Fetch Details pipeline.
// MAX_POLLS × INTERVAL = how long the UI will spin before giving up locally.
// MAX_TRANSIENT_FAILURES = how many consecutive network errors we tolerate
// before treating the pipeline as lost (a single 500 from a Cloud Run cold
// start used to kill polling — now we forgive a few in a row).
const FETCH_DETAILS_POLL_INTERVAL_MS      = 1500;
const FETCH_DETAILS_MAX_POLLS             = 200;  // ≈ 5 minutes of polling
const FETCH_DETAILS_MAX_TRANSIENT_FAILURES = 3;

// section_selectors ("Import Sections") was removed from the pipeline. Filter it
// out of any server-provided step list so stale last_run records don't show it.
const stripImportSections = (steps) => (steps || []).filter((s) => s.id !== 'section_selectors');

const DomainsAndUrls = ({ ViewedPage, shop_domain, app_url, isInSettingsContainer, hideFetchDetails = false }) => {
  useEffect(() => {
    ViewedPage(44);
  }, []);

  // Website pages — committed = in DB, pending = working draft
  const [committedWebsitePages, setCommittedWebsitePages] = useState([]);
  const [pendingWebsitePages, setPendingWebsitePages] = useState([]);
  const [fetchedWebsitePages, setFetchedWebsitePages] = useState([]);
  const [fetchingPageLinks, setFetchingPageLinks] = useState(false);
  const [pageLinksUrl, setPageLinksUrl] = useState('');
  const [manualWebsiteUrl, setManualWebsiteUrl] = useState('');
  const [savingWebsitePages, setSavingWebsitePages] = useState(false);

  // Landing pages — committed = in DB, pending = working draft
  const [committedLandingPages, setCommittedLandingPages] = useState([]);
  const [pendingLandingPages, setPendingLandingPages] = useState([]);
  const [availableLandingPages, setAvailableLandingPages] = useState([]);
  const [loadingLandingPages, setLoadingLandingPages] = useState(false);
  const [landingPagesLoaded, setLandingPagesLoaded] = useState(false);
  const [manualLandingUrl, setManualLandingUrl] = useState('');
  const [savingLandingPages, setSavingLandingPages] = useState(false);

  // Fetch Details pipeline
  const PIPELINE_STEPS = [
    { id: 'context_images',    label: 'Context, Images & Branding' },
    { id: 'summarization',     label: 'Context Summarization'      },
    { id: 'youtube',           label: 'YouTube Data'               },
    { id: 'section_json',      label: 'Section JSON'               },
  ];
  const [fetchDetailsSteps, setFetchDetailsSteps]     = useState([]);
  const [fetchDetailsRunning, setFetchDetailsRunning] = useState(false);
  // Once the pipeline has completed at least once (succeeded or failed), the
  // one-time Fetch Details button is locked — re-runs go through Re-run Modules.
  const [fetchDetailsHasRun, setFetchDetailsHasRun]   = useState(false);
  // Refs for the polling lifecycle. Refs (not state) because we mutate them
  // synchronously from event handlers and don't need rerenders on change.
  const fetchDetailsPollRef     = useRef(null);   // interval handle
  const fetchDetailsInFlightRef = useRef(false);  // double-click guard for handleFetchDetails
  const pollCountRef            = useRef(0);      // total polls in current cycle
  const pollFailuresRef         = useRef(0);      // consecutive transient failures

  // ── Loaders ──────────────────────────────────────────────────────────────

  const loadSelectedPages = async () => {
    try {
      const res = await axios.get(`${app_url}/api/settings/domains-and-urls/selected-pages`, { withCredentials: true });
      if (res.data.success) {
        const wp = res.data.website_pages || [];
        const lp = res.data.landing_pages || [];
        setCommittedWebsitePages(wp);
        setPendingWebsitePages([...wp]);
        setCommittedLandingPages(lp);
        setPendingLandingPages([...lp]);
        setFetchedWebsitePages(res.data.fetched_website_pages || []);
      }
    } catch {
      // Non-fatal
    }
  };

  const loadLandingPages = async () => {
    try {
      setLoadingLandingPages(true);
      const res = await axios.get(`${app_url}/api/settings/domains-and-urls/landing-pages`, { withCredentials: true });
      if (res.data.success) setAvailableLandingPages(res.data.landing_pages || []);
    } catch {
      // BigQuery may not be configured
    } finally {
      setLoadingLandingPages(false);
      setLandingPagesLoaded(true);
    }
  };

  // Single point of polling lifecycle — safe to call from anywhere that
  // needs to stop the interval (succeed/fail/timeout/unmount/manual retry).
  const stopPolling = () => {
    if (fetchDetailsPollRef.current) {
      clearInterval(fetchDetailsPollRef.current);
      fetchDetailsPollRef.current = null;
    }
  };

  const startPolling = (jobId) => {
    stopPolling();
    pollCountRef.current    = 0;
    pollFailuresRef.current = 0;
    fetchDetailsPollRef.current = setInterval(async () => {
      pollCountRef.current += 1;

      // Hard cap so the UI doesn't spin forever. The next page load will
      // resume polling via loadFetchDetailsStatus() if the job is still live.
      if (pollCountRef.current > FETCH_DETAILS_MAX_POLLS) {
        stopPolling();
        setFetchDetailsRunning(false);
        toast.error('Still running — close this and check back later');
        return;
      }

      try {
        const poll = await axios.get(`${app_url}/api/firebase/job_status/${jobId}`, { withCredentials: true });
        const job  = poll.data?.job;

        // Job has disappeared from Redis (TTL expired) or the orchestrator
        // crashed without writing terminal status. Treat as terminal "lost"
        // — clearing the interval is the whole point of this fix.
        if (!job) {
          stopPolling();
          setFetchDetailsRunning(false);
          toast.error('Pipeline lost — please retry');
          return;
        }

        pollFailuresRef.current = 0; // reset on any successful response

        if (job.steps) setFetchDetailsSteps(stripImportSections(job.steps));

        if (job.status === 'succeeded') {
          stopPolling();
          setFetchDetailsRunning(false);
          setFetchDetailsHasRun(true);
          toast.success('All details fetched successfully');
        } else if (job.status === 'failed') {
          stopPolling();
          setFetchDetailsRunning(false);
          setFetchDetailsHasRun(true);
          toast.error('One or more steps failed. Check the status panel for details.');
        }
      } catch {
        // Tolerate transient errors (Cloud Run cold starts, brief network
        // hiccups). Only give up after several consecutive failures.
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= FETCH_DETAILS_MAX_TRANSIENT_FAILURES) {
          stopPolling();
          setFetchDetailsRunning(false);
          toast.error('Lost connection while polling fetch status');
        }
      }
    }, FETCH_DETAILS_POLL_INTERVAL_MS);
  };

  const loadFetchDetailsStatus = async () => {
    if (hideFetchDetails) return;
    try {
      const res = await axios.get(`${app_url}/api/settings/domains-and-urls/fetch-details/status`, { withCredentials: true });
      if (!res.data.success) return;

      // Any persisted last_run means the pipeline has already been run once —
      // lock the one-time Fetch Details button (re-runs go through Re-run Modules).
      if (res.data.last_run) setFetchDetailsHasRun(true);

      // Active job in progress — resume polling and show live steps
      if (res.data.active_job?.jobId) {
        const { jobId, steps } = res.data.active_job;
        if (steps) setFetchDetailsSteps(stripImportSections(steps));
        setFetchDetailsRunning(true);
        startPolling(jobId);
        return;
      }

      // No active job — restore last completed run steps
      if (res.data.last_run?.steps) {
        setFetchDetailsSteps(stripImportSections(res.data.last_run.steps));
      }
    } catch {
      // Non-fatal
    }
  };

  useEffect(() => {
    if (shop_domain && app_url) {
      loadSelectedPages();
      loadLandingPages();
      loadFetchDetailsStatus();
    }
  }, [shop_domain, app_url]);

  // Clean up poll interval on unmount
  useEffect(() => () => stopPolling(), []);

  // ── Website page handlers ─────────────────────────────────────────────────

  const handleFetchPageLinks = async () => {
    const trimmed = pageLinksUrl.trim();
    if (!trimmed) { toast.error('Enter a URL to fetch pages from'); return; }
    try { new URL(trimmed); } catch { toast.error('Please enter a valid URL (e.g. https://example.com)'); return; }
    try {
      setFetchingPageLinks(true);
      const res = await axios.post(
        `${app_url}/api/settings/domains-and-urls/fetch-page-links`,
        { url: trimmed },
        { withCredentials: true }
      );
      if (res.data.success) {
        setFetchedWebsitePages(res.data.urls || []);
        if ((res.data.urls || []).length === 0) toast.error('No pages found on that URL. Try a different URL.');
        else toast.success(`${res.data.total} pages fetched`);
      } else {
        toast.error(res.data.message || 'Failed to fetch pages');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error fetching pages. Please try again.');
    } finally {
      setFetchingPageLinks(false);
    }
  };

  const addToPendingWebsite = (url) => {
    if (!url) return;
    if (pendingWebsitePages.includes(url)) { toast.error('This page is already in your selection'); return; }
    if (pendingWebsitePages.length >= WEBSITE_PAGES_LIMIT) {
      toast.error(`Maximum of ${WEBSITE_PAGES_LIMIT} website pages allowed`);
      return;
    }
    setPendingWebsitePages(prev => [...prev, url]);
  };

  const removeFromPendingWebsite = (url) => {
    setPendingWebsitePages(prev => prev.filter(u => u !== url));
  };

  const handleSelectWebsitePage = (e) => {
    addToPendingWebsite(e.target.value);
    e.target.value = '';
  };

  const handleAddWebsiteUrlManually = (e) => {
    e.preventDefault();
    const trimmed = manualWebsiteUrl.trim();
    if (!trimmed) { toast.error('Please enter a URL'); return; }
    try { new URL(trimmed); } catch { toast.error('Please enter a valid URL (e.g. https://example.com)'); return; }
    addToPendingWebsite(trimmed);
    setManualWebsiteUrl('');
  };

  const handleSaveWebsitePages = async () => {
    try {
      setSavingWebsitePages(true);
      const res = await axios.put(
        `${app_url}/api/settings/domains-and-urls/website-pages`,
        { urls: pendingWebsitePages },
        { withCredentials: true }
      );
      if (res.data.success) {
        setCommittedWebsitePages(res.data.website_pages);
        setPendingWebsitePages([...res.data.website_pages]);
        toast.success('Website pages saved');
      } else {
        toast.error(res.data.message || 'Failed to save website pages');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error saving website pages');
    } finally {
      setSavingWebsitePages(false);
    }
  };

  // ── Landing page handlers ─────────────────────────────────────────────────

  const addToPendingLanding = (url) => {
    if (!url) return;
    if (pendingLandingPages.includes(url)) { toast.error('This page is already in your selection'); return; }
    if (pendingLandingPages.length >= LANDING_PAGES_LIMIT) {
      toast.error(`Maximum of ${LANDING_PAGES_LIMIT} landing pages allowed`);
      return;
    }
    setPendingLandingPages(prev => [...prev, url]);
  };

  const removeFromPendingLanding = (url) => {
    setPendingLandingPages(prev => prev.filter(u => u !== url));
  };

  const handleSelectLandingPage = (e) => {
    addToPendingLanding(e.target.value);
    e.target.value = '';
  };

  const handleAddLandingUrlManually = (e) => {
    e.preventDefault();
    const trimmed = manualLandingUrl.trim();
    if (!trimmed) { toast.error('Please enter a URL'); return; }
    try { new URL(trimmed); } catch { toast.error('Please enter a valid URL (e.g. https://example.com)'); return; }
    addToPendingLanding(trimmed);
    setManualLandingUrl('');
  };

  const handleSaveLandingPages = async () => {
    try {
      setSavingLandingPages(true);
      const res = await axios.put(
        `${app_url}/api/settings/domains-and-urls/landing-pages`,
        { urls: pendingLandingPages },
        { withCredentials: true }
      );
      if (res.data.success) {
        setCommittedLandingPages(res.data.landing_pages);
        setPendingLandingPages([...res.data.landing_pages]);
        toast.success('Landing pages saved');
      } else {
        toast.error(res.data.message || 'Failed to save landing pages');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error saving landing pages');
    } finally {
      setSavingLandingPages(false);
    }
  };

  // ── Fetch Details handler ────────────────────────────────────────────────

  const handleFetchDetails = async () => {
    // Synchronous double-click guard. React state updates are async, so two
    // rapid clicks could both observe `fetchDetailsRunning === false` and
    // both POST. A ref flips immediately and blocks the second call.
    if (fetchDetailsInFlightRef.current) return;
    if (fetchDetailsHasRun) {
      toast.error('Details already fetched. Use the Re-run Modules tab to re-run.');
      return;
    }
    if (committedWebsitePages.length + committedLandingPages.length === 0) {
      toast.error('Please save at least one website page or landing page before fetching details.');
      return;
    }
    fetchDetailsInFlightRef.current = true;
    try {
      setFetchDetailsRunning(true);
      setFetchDetailsSteps(PIPELINE_STEPS.map(s => ({ ...s, status: 'pending' })));

      const res = await axios.post(
        `${app_url}/api/settings/domains-and-urls/fetch-details`,
        {},
        { withCredentials: true }
      );

      if (res.status !== 202 || !res.data?.jobId) {
        toast.error(res.data?.message || 'Failed to start fetch details');
        setFetchDetailsRunning(false);
        return;
      }

      startPolling(res.data.jobId);
    } catch (err) {
      // 409 = a fetch is already running. Reuse the existing jobId and start
      // polling that — don't spawn a second orchestrator (P2-3).
      if (err?.response?.status === 409 && err?.response?.data?.jobId) {
        toast.error('A fetch is already in progress — resuming');
        startPolling(err.response.data.jobId);
        return;
      }
      setFetchDetailsRunning(false);
      toast.error(err?.response?.data?.message || 'Error starting fetch details');
    } finally {
      fetchDetailsInFlightRef.current = false;
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────

  const websiteFullyLocked = committedWebsitePages.length >= WEBSITE_PAGES_LIMIT;
  const landingFullyLocked = committedLandingPages.length >= LANDING_PAGES_LIMIT;
  const websiteHasSaved = committedWebsitePages.length > 0;
  const landingHasSaved = committedLandingPages.length > 0;
  const websiteNewPending = pendingWebsitePages.filter(u => !committedWebsitePages.includes(u));
  const landingNewPending = pendingLandingPages.filter(u => !committedLandingPages.includes(u));
  const websiteIsDirty = websiteNewPending.length > 0;
  const landingIsDirty = landingNewPending.length > 0;
  const websiteAtLimit = pendingWebsitePages.length >= WEBSITE_PAGES_LIMIT;
  const landingAtLimit = pendingLandingPages.length >= LANDING_PAGES_LIMIT;
  const unpickedFetchedPages = fetchedWebsitePages.filter(u => !pendingWebsitePages.includes(u));
  const unpickedLandingPages = availableLandingPages.filter(u => !pendingLandingPages.includes(u));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container fluid className="p-0">
      <div className='main-title mt-4'>
        <h2>Domains & URLs</h2>
      </div>

      <div className="main-accordion-body mt-4 p-2">

        {/* ── Row 1: Fetch Website Pages ── */}
        <Card className="mb-3 asset-form-card">
          <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
            <div className="sxi-accordion-header-with-icon">
              <div className="sxi-accordion-header-icon" aria-hidden="true">
                <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                  <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" stroke="#CEDEFF"/>
                  <circle cx="27" cy="27" r="11" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                  <ellipse cx="27" cy="27" rx="5.5" ry="11" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                  <line x1="16" y1="27" x2="38" y2="27" stroke="#2056C1" strokeWidth="1.5"/>
                </svg>
              </div>
              <span>Fetch Website Pages</span>
            </div>
          </Card.Header>
          <Card.Body>
            {fetchedWebsitePages.length === 0 ? (
              <>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="url"
                    placeholder="https://example.com"
                    value={pageLinksUrl}
                    onChange={e => setPageLinksUrl(e.target.value)}
                    disabled={fetchingPageLinks}
                  />
                  <Button
                    className="sxi_primary_blue_btn btn-height"
                    onClick={handleFetchPageLinks}
                    disabled={fetchingPageLinks || !pageLinksUrl.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {fetchingPageLinks
                      ? <><Spinner size="sm" animation="border" className="me-2" />Fetching...</>
                      : 'Fetch Pages'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span style={{ color: '#198754', fontWeight: 600 }}>✓</span>
                <span className="text-muted small">Pages fetched</span>
                <Badge bg="secondary">{fetchedWebsitePages.length} pages found</Badge>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ── Row 2: Select or Add Website Pages ── */}
        <Card className="mb-3 asset-form-card">
          <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="sxi-accordion-header-with-icon">
                <div className="sxi-accordion-header-icon" aria-hidden="true">
                  <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                    <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" stroke="#CEDEFF"/>
                    <rect x="18" y="15" width="18" height="24" rx="2" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                    <line x1="22" y1="21" x2="32" y2="21" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="22" y1="25" x2="32" y2="25" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="22" y1="29" x2="29" y2="29" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <span>Website Pages</span>
              </div>
              <span style={{ fontSize: 12, color: '#667085', background: '#F2F4F7', border: '1px solid #D0D5DD', borderRadius: 100, padding: '2px 10px' }}>
                {pendingWebsitePages.length}/{WEBSITE_PAGES_LIMIT}
              </span>
            </div>
          </Card.Header>
          <Card.Body>
            {websiteFullyLocked ? (
              <>
                <div className="d-flex flex-wrap gap-2">
                  {committedWebsitePages.map((url, i) => (
                    <span key={i} style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 100, background: '#F2F4F7', color: '#344054', border: '1px solid #D0D5DD', fontSize: 12, wordBreak: 'break-all', maxWidth: '100%' }}>
                      {url}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <>
                <Row className="align-items-start mb-3">
                  {/* Left: fetched pages dropdown */}
                  <Col md={5}>
                    {fetchedWebsitePages.length === 0 ? (
                      <Form.Select disabled>
                        <option>Fetch pages first</option>
                      </Form.Select>
                    ) : unpickedFetchedPages.length === 0 ? (
                      <p className="text-muted small mb-0">All fetched pages selected.</p>
                    ) : (
                      <Form.Select onChange={handleSelectWebsitePage} defaultValue="" disabled={websiteAtLimit}>
                        <option value="">— choose a page —</option>
                        {unpickedFetchedPages.map((url, i) => (
                          <option key={i} value={url}>{url}</option>
                        ))}
                      </Form.Select>
                    )}
                  </Col>

                  {/* OR divider */}
                  <Col md={2} className="d-flex align-items-center justify-content-center" style={{ paddingTop: '1.65rem' }}>
                    <span className="text-muted fw-semibold">or</span>
                  </Col>

                  {/* Right: manual add */}
                  <Col md={5}>
                    <Form onSubmit={handleAddWebsiteUrlManually}>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="url"
                          placeholder="https://example.com/page"
                          value={manualWebsiteUrl}
                          onChange={e => setManualWebsiteUrl(e.target.value)}
                          disabled={websiteAtLimit}
                        />
                        <Button
                          type="submit"
                          className="back-button btn-height"
                          disabled={!manualWebsiteUrl.trim() || websiteAtLimit}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Add
                        </Button>
                      </div>
                    </Form>
                  </Col>
                </Row>

                {websiteHasSaved && (
                  <div className="mb-2">
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {committedWebsitePages.map((url, i) => (
                        <span key={i} style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 100, background: '#F2F4F7', color: '#344054', border: '1px solid #D0D5DD', fontSize: 12, wordBreak: 'break-all', maxWidth: '100%' }}>
                          {url}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {websiteNewPending.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2">
                      {websiteNewPending.map((url, i) => (
                        <span
                          key={i}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px 3px 10px', borderRadius: 100, background: '#F2F4F7', color: '#344054', border: '1px solid #D0D5DD', fontSize: 12, maxWidth: '100%' }}
                        >
                          <span style={{ wordBreak: 'break-all' }}>{url}</span>
                          <button
                            type="button"
                            onClick={() => removeFromPendingWebsite(url)}
                            style={{ background: 'none', border: 'none', color: '#667085', cursor: 'pointer', padding: '0 0 0 2px', lineHeight: 1, fontSize: '0.8rem' }}
                            aria-label="Remove"
                          >✕</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}


                <Button
                  className="sxi_primary_blue_btn btn-height"
                  onClick={handleSaveWebsitePages}
                  disabled={!websiteIsDirty || savingWebsitePages}
                >
                  {savingWebsitePages
                    ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</>
                    : 'Save Website Pages'}
                </Button>
              </>
            )}
          </Card.Body>
        </Card>

        {/* ── Row 3: Select or Add Landing Pages ── */}
        <Card className="mb-3 asset-form-card">
          <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="sxi-accordion-header-with-icon">
                <div className="sxi-accordion-header-icon" aria-hidden="true">
                  <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                    <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" stroke="#CEDEFF"/>
                    <circle cx="27" cy="27" r="10" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                    <circle cx="27" cy="27" r="4" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                    <line x1="27" y1="14" x2="27" y2="17" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="27" y1="37" x2="27" y2="40" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="14" y1="27" x2="17" y2="27" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="37" y1="27" x2="40" y2="27" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <span>Landing Pages</span>
              </div>
              <span style={{ fontSize: 12, color: '#667085', background: '#F2F4F7', border: '1px solid #D0D5DD', borderRadius: 100, padding: '2px 10px' }}>
                {pendingLandingPages.length}/{LANDING_PAGES_LIMIT}
              </span>
            </div>
          </Card.Header>
          <Card.Body>
            {landingFullyLocked ? (
              <>
                <div className="d-flex flex-wrap gap-2">
                  {committedLandingPages.map((url, i) => (
                    <span key={i} style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 100, background: '#F2F4F7', color: '#344054', border: '1px solid #D0D5DD', fontSize: 12, wordBreak: 'break-all', maxWidth: '100%' }}>
                      {url}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <>
                <Row className="align-items-start mb-3">
                  {/* Left: BigQuery dropdown */}
                  <Col md={5}>
                    {loadingLandingPages ? (
                      <div className="d-flex align-items-center gap-2">
                        <Spinner animation="border" size="sm" />
                        <span className="text-muted small">Loading...</span>
                      </div>
                    ) : !landingPagesLoaded ? null : availableLandingPages.length === 0 ? (
                      <p className="text-muted small mb-0">No Google Ads data found.</p>
                    ) : unpickedLandingPages.length === 0 ? (
                      <p className="text-muted small mb-0">All pages selected.</p>
                    ) : (
                      <Form.Select onChange={handleSelectLandingPage} defaultValue="" disabled={landingAtLimit}>
                        <option value="">— choose a landing page —</option>
                        {unpickedLandingPages.map((url, i) => (
                          <option key={i} value={url}>{url}</option>
                        ))}
                      </Form.Select>
                    )}
                  </Col>

                  {/* OR divider */}
                  <Col md={2} className="d-flex align-items-center justify-content-center" style={{ paddingTop: '1.65rem' }}>
                    <span className="text-muted fw-semibold">or</span>
                  </Col>

                  {/* Right: manual add */}
                  <Col md={5}>
                    <Form onSubmit={handleAddLandingUrlManually}>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="url"
                          placeholder="https://example.com/landing"
                          value={manualLandingUrl}
                          onChange={e => setManualLandingUrl(e.target.value)}
                          disabled={landingAtLimit}
                        />
                        <Button
                          type="submit"
                          className="back-button btn-height"
                          disabled={!manualLandingUrl.trim() || landingAtLimit}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Add
                        </Button>
                      </div>
                    </Form>
                  </Col>
                </Row>

                {landingHasSaved && (
                  <div className="mb-2">
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {committedLandingPages.map((url, i) => (
                        <span key={i} style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 100, background: '#F2F4F7', color: '#344054', border: '1px solid #D0D5DD', fontSize: 12, wordBreak: 'break-all', maxWidth: '100%' }}>
                          {url}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {landingNewPending.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2">
                      {landingNewPending.map((url, i) => (
                        <span
                          key={i}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px 3px 10px', borderRadius: 100, background: '#F2F4F7', color: '#344054', border: '1px solid #D0D5DD', fontSize: 12, maxWidth: '100%' }}
                        >
                          <span style={{ wordBreak: 'break-all' }}>{url}</span>
                          <button
                            type="button"
                            onClick={() => removeFromPendingLanding(url)}
                            style={{ background: 'none', border: 'none', color: '#667085', cursor: 'pointer', padding: '0 0 0 2px', lineHeight: 1, fontSize: '0.8rem' }}
                            aria-label="Remove"
                          >✕</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}


                <Button
                  className="sxi_primary_blue_btn btn-height"
                  onClick={handleSaveLandingPages}
                  disabled={!landingIsDirty || savingLandingPages}
                >
                  {savingLandingPages
                    ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</>
                    : 'Save Landing Pages'}
                </Button>
              </>
            )}
          </Card.Body>
        </Card>

        {/* ── Row 4: Fetch Details ── */}
        {!hideFetchDetails && (
        <Card className="mb-4 asset-form-card">
          <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
            <div className="sxi-accordion-header-with-icon">
              <div className="sxi-accordion-header-icon" aria-hidden="true">
                <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                  <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" stroke="#CEDEFF"/>
                  <circle cx="27" cy="27" r="5" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                  <line x1="27" y1="15" x2="27" y2="19" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="27" y1="35" x2="27" y2="39" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="15" y1="27" x2="19" y2="27" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="35" y1="27" x2="39" y2="27" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="18.8" y1="18.8" x2="21.6" y2="21.6" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="32.4" y1="32.4" x2="35.2" y2="35.2" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="35.2" y1="18.8" x2="32.4" y2="21.6" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="21.6" y1="32.4" x2="18.8" y2="35.2" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span>Fetch Details</span>
            </div>
          </Card.Header>
          <Card.Body>
            <Button
              className="sxi_primary_blue_btn btn-height"
              onClick={handleFetchDetails}
              disabled={fetchDetailsRunning || fetchDetailsHasRun}
            >
              {fetchDetailsRunning
                ? <><Spinner size="sm" animation="border" className="me-2" />Running Pipeline...</>
                : 'Fetch Details'}
            </Button>
            {fetchDetailsHasRun && !fetchDetailsRunning && (
              <p className="text-muted small mt-2 mb-0">
                Details already fetched. To re-run, use the Re-run Modules tab.
              </p>
            )}

            {fetchDetailsSteps.length > 0 && (
              <div className="mt-4">
                {fetchDetailsSteps.map((step) => {
                  const icons  = { pending: '○', running: '⏳', completed: '✓', failed: '✗' };
                  const colors = { pending: '#888', running: '#0d6efd', completed: '#198754', failed: '#dc3545' };
                  return (
                    <div key={step.id} className="d-flex align-items-start gap-2 mb-2">
                      <span style={{ color: colors[step.status] || '#888', fontWeight: 600, minWidth: '1.2rem', fontSize: '1rem', lineHeight: '1.5' }}>
                        {icons[step.status] || '○'}
                      </span>
                      <div>
                        <span style={{ color: colors[step.status] || '#888', fontWeight: step.status === 'running' ? 600 : 400 }}>
                          {step.label}
                        </span>
                        {step.status === 'running' && (
                          <Spinner size="sm" animation="border" className="ms-2" style={{ width: '0.75rem', height: '0.75rem' }} />
                        )}
                        {step.error && <div className="text-danger small mt-1">{step.error}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>
        )}

      </div>
    </Container>
  );
};

export default DomainsAndUrls;
